import { useMemo, useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    SiteState,
    TotadminPermission,
    useAssignSitesToDeliveryZoneMutation,
    useGetSitesQuery,
} from 'data/__generated__';

import { DataTable } from 'components/DataTable';
import { SectionContainer } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { allSitesColumns, type AllSiteRow, type ZoneSiteRow } from '../constants/zoneSitesColumns';

/**
 * Ajouter des sites à la zone : la seule action de la section demande `DeliveryZonesManage`, elle est donc
 * verrouillée sans ce droit — et le catalogue de sites (des milliers) n'est chargé que si elle est montée.
 */
export const AddSitesSection = ({
    deliveryZoneId,
    zoneSites,
    onSitesAssigned,
}: {
    deliveryZoneId: string;
    zoneSites: ZoneSiteRow[];
    onSitesAssigned: () => Promise<unknown>;
}) => (
    <SectionContainer
        title="Ajouter des sites"
        isInitiallyOpen
        requiredPermissions={[TotadminPermission.DeliveryZonesManage]}
    >
        <AddSitesContent deliveryZoneId={deliveryZoneId} zoneSites={zoneSites} onSitesAssigned={onSitesAssigned} />
    </SectionContainer>
);

const AddSitesContent = ({
    deliveryZoneId,
    zoneSites,
    onSitesAssigned,
}: {
    deliveryZoneId: string;
    zoneSites: ZoneSiteRow[];
    onSitesAssigned: () => Promise<unknown>;
}) => {
    const { data: allSitesData, loading: allSitesLoading, refetch: refetchAllSites } = useGetSitesQuery();
    const [assignSitesToDeliveryZone, { loading: assignLoading }] = useAssignSitesToDeliveryZoneMutation();
    const [allSitesSelected, setAllSitesSelected] = useState<string[]>([]);

    // Memoized: runs over thousands of sites — without useMemo every render re-filtered them and handed a new
    // array ref to the DataTable.
    const allSites = useMemo<AllSiteRow[]>(() => {
        const zoneSiteIds = new Set(zoneSites.map((site) => site._id));
        return (allSitesData?.sites ?? []).filter(
            (site) => site.state === SiteState.Regular && !zoneSiteIds.has(site._id),
        );
    }, [allSitesData, zoneSites]);

    const handleAddSelected = async () => {
        if (allSitesSelected.length === 0) return;
        try {
            await assignSitesToDeliveryZone({ variables: { deliveryZoneId, siteIds: allSitesSelected } });
            toast.success(`${allSitesSelected.length} site(s) ajouté(s) à la zone`);
            setAllSitesSelected([]);
            await Promise.all([onSitesAssigned(), refetchAllSites()]);
        } catch {
            toast.error('Une erreur est survenue lors de l’ajout des sites');
        }
    };

    return (
        <TableWrapper>
            <DataTable<AllSiteRow>
                columns={allSitesColumns}
                data={allSites}
                filterMode="client"
                disableUrlSync
                isLoading={allSitesLoading && !allSitesData}
                getRowId={(row) => row._id}
                searchPlaceholder="Nom du site ou de l'organisation"
                autoFocusSearch={false}
                enableSelection
                selectedKeys={allSitesSelected}
                onSelectionChange={setAllSitesSelected}
                toolbarActions={
                    allSitesSelected.length > 0 ? (
                        <TotemPrimaryButton
                            onClick={handleAddSelected}
                            disabled={assignLoading}
                            requiredPermissions={[TotadminPermission.DeliveryZonesManage]}
                        >
                            Ajouter à la zone ({allSitesSelected.length})
                        </TotemPrimaryButton>
                    ) : null
                }
            />
        </TableWrapper>
    );
};

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 420px;
    overflow: hidden;
`;
